import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ContentChildren,
  QueryList,
  AfterContentInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { StepperType } from './dtos/stepper-type.type';
import { StepConfig } from './dtos/step-config.interface';
import { StepperEvent } from './dtos/stepper-events.interface';

@Component({
  selector: 'aprova-stepper',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepperComponent implements OnInit, OnChanges, AfterContentInit {
  @Input()
  public type: StepperType = 'bullets';

  @Input()
  public steps: StepConfig[] = [];

  @Input()
  public currentStep: number = 0;

  @Input()
  public showStepLabels: boolean = true;

  @Input()
  public allowDirectNavigation: boolean = false;

  @Input()
  public allowFutureNavigation: boolean = false;

  @Output()
  public stepChange = new EventEmitter<StepperEvent>();

  @Output()
  public flowComplete = new EventEmitter<StepperEvent>();

  @Output()
  public stepClick = new EventEmitter<StepperEvent>();

  @ContentChildren('stepContent')
  public stepContentTemplates?: QueryList<TemplateRef<any>>;

  protected stepNumbers: number[] = [];

  protected stepTemplates: TemplateRef<any>[] = [];

  ngOnInit(): void {
    this.updateStepNumbers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['steps']) {
      this.updateStepNumbers();
    }
  }

  ngAfterContentInit(): void {
    if (this.stepContentTemplates) {
      this.stepTemplates = this.stepContentTemplates.toArray();
    }
  }

  private updateStepNumbers(): void {
    this.stepNumbers = Array.from({ length: this.steps.length }, (_, i) => i);
  }

  protected isStepActive(stepIndex: number): boolean {
    return stepIndex === this.currentStep;
  }

  protected isStepCompleted(stepIndex: number): boolean {
    return stepIndex < this.currentStep;
  }

  protected isStepEnabled(stepIndex: number): boolean {
    const step = this.steps[stepIndex];

    if (step?.enabled === false) {
      return false;
    }

    if (this.allowDirectNavigation) {
      return true;
    }

    if (this.allowFutureNavigation) {
      return stepIndex <= this.currentStep + 1;
    }

    return stepIndex <= this.currentStep;
  }

  public nextStep(): void {
    if (this.currentStep < this.steps.length - 1) {
      this.goToStep(this.currentStep + 1);
    }
  }

  public previousStep(): void {
    if (this.currentStep > 0) {
      this.goToStep(this.currentStep - 1);
    }
  }

  public goToStep(stepIndex: number): void {
    if (this.isValidStepIndex(stepIndex) && this.isStepEnabled(stepIndex)) {
      const previousStep = this.currentStep;
      this.currentStep = stepIndex;

      this.stepChange.emit({
        currentStep: stepIndex,
        previousStep,
      });
    }
  }

  public completeFlow(): void {
    this.flowComplete.emit({
      currentStep: this.currentStep,
      previousStep: this.currentStep,
      isCompleted: true,
    });
  }

  protected onStepClick(stepIndex: number): void {
    if (this.isStepEnabled(stepIndex)) {
      this.stepClick.emit({
        currentStep: stepIndex,
        previousStep: this.currentStep,
      });

      if (this.allowDirectNavigation || stepIndex <= this.currentStep) {
        this.goToStep(stepIndex);
      }
    }
  }

  private isValidStepIndex(stepIndex: number): boolean {
    return stepIndex >= 0 && stepIndex < this.steps.length;
  }

  protected getStepTemplate(stepIndex: number): TemplateRef<any> | null {
    return this.stepTemplates[stepIndex] || null;
  }

  protected hasStepTemplate(stepIndex: number): boolean {
    return this.getStepTemplate(stepIndex) !== null;
  }

  protected getStepConfig(stepIndex: number): StepConfig | null {
    return this.steps[stepIndex] || null;
  }

  protected trackByStepIndex(index: number, stepIndex: number): number {
    return stepIndex;
  }

  protected hasStepLabels(): boolean {
    return this.steps.some((step) => step.title);
  }

  protected getProgressWidth(): string {
    if (this.steps.length <= 1 || this.currentStep === 0) {
      return '0%';
    }

    const totalSteps = this.steps.length;
    const progressPerStep = 100 / (totalSteps - 1);
    const progress = this.currentStep * progressPerStep;

    return `${Math.min(progress, 100)}%`;
  }
}
